import { Invite, OrganizationResponse } from "../interfaces/table";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  parseReduxQueryError,
  customBaseQuery,
} from "../../common/reduxQuery/helpers";
import { setOrganizationResponse } from "./UserManagementSlice";

export const userManagementApi = createApi({
  reducerPath: "userManagementApi",
  baseQuery: customBaseQuery(),
  endpoints: (build) => ({
    fetchMembers: build.query<OrganizationResponse, string>({
      query: (organizationId) => ({
        url: `/api/v1/user/management/${organizationId}`,
        method: "GET",
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setOrganizationResponse(data));
        } catch (err) {
          console.error(
            `An error occurred while fetching the members: ${parseReduxQueryError(
              err,
            )}`,
          );
          dispatch(
            setOrganizationResponse({ members: [], inputs: [], roles: [] }),
          );
        }
      },
    }),
    fetchInvites: build.query<Array<Invite>, string>({
      query: (organizationId) => ({
        url: `/api/v1/user/management/${organizationId}/invites`,
        method: "GET",
      }),
    }),
    inviteMember: build.mutation<
      void,
      {
        organizationId: string;
        clientId: string;
        email: string;
        permissions: Array<string>;
      }
    >({
      query: ({ organizationId, clientId, email, permissions }) => ({
        url: `/api/v1/user/management/${organizationId}/member/invite`,
        body: {
          clientId,
          memberEmail: email,
          permissions,
        },
        method: "POST",
      }),
    }),
    deleteInvite: build.mutation<
      void,
      { organizationId: string; inviteId: string; email: string }
    >({
      query: ({ organizationId, inviteId, email }) => ({
        url: `/api/v1/user/management/${organizationId}/invites`,
        body: {
          inviteId,
          email,
        },
        method: "POST",
      }),
    }),
    deleteUserFromOrgApi: build.mutation<
      void,
      { organizationId: string; memberId: string }
    >({
      query: ({ organizationId, memberId }) => ({
        url: `/api/v1/user/management/${organizationId}/member/${encodeURIComponent(
          memberId,
        )}`,
        method: "DELETE",
      }),
    }),
    deleteRoleApi: build.mutation<
      void,
      {
        organizationId: string;
        memberId: string;
        data: { roles: Array<string> };
      }
    >({
      query: ({ organizationId, memberId, data }) => ({
        url: `/api/v1/user/management/${organizationId}/member/${encodeURIComponent(
          memberId,
        )}/roles`,
        body: data,
        method: "DELETE",
      }),
    }),
    addRoleApi: build.mutation<
      void,
      {
        organizationId: string;
        memberId: string;
        data: { roles: Array<string> };
      }
    >({
      query: ({ organizationId, memberId, data }) => ({
        url: `/api/v1/user/management/${organizationId}/member/${encodeURIComponent(
          memberId,
        )}/roles`,
        body: data,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useFetchMembersQuery,
  useLazyFetchInvitesQuery,
  useInviteMemberMutation,
  useDeleteInviteMutation,
  useDeleteUserFromOrgApiMutation,
  useDeleteRoleApiMutation,
  useAddRoleApiMutation,
} = userManagementApi;
